import React, { useState, useEffect } from 'react';
import { Card, Table, Button, Input, InputNumber, Select, Tag, Space, Drawer, Descriptions, message, Form, Typography, Modal, Spin, Upload, Popconfirm, DatePicker } from 'antd';
import { SearchOutlined, PlusOutlined, EyeOutlined, EditOutlined, FileTextOutlined, UploadOutlined, RollbackOutlined, DownloadOutlined, LinkOutlined, DisconnectOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { rebillApi, customerApi, authApi, appealApi } from '../services/api';

const { Text, Paragraph } = Typography;

const STATUS_OPTIONS = [
  { value: '', label: '全部状态' },
  { value: 'registered', label: '已登记' },
  { value: 'awaiting_cs', label: '客服处理中' },
  { value: 'cs_replied', label: '已回财务' },
  { value: 'closed', label: '已结案' },
];
const STATUS_COLOR = { registered: 'gold', awaiting_cs: 'blue', cs_replied: 'purple', closed: 'green' };
const STATUS_TEXT = { registered: '已登记', awaiting_cs: '客服处理中', cs_replied: '已回财务', closed: '已结案' };

const BEARING_OPTIONS = [
  { value: 'internal', label: '我方承担' },
  { value: 'customer', label: '客户承担' },
  { value: 'mixed', label: '部分我方部分客户' },
  { value: 'carrier', label: '承运商承担' },
  { value: 'insurance', label: '保险/索赔' },
  { value: 'other', label: '其他' },
];
const BEARING_TEXT = Object.fromEntries(BEARING_OPTIONS.map(o => [o.value, o.label]));

function amountFmt(v) {
  if (v === null || v === undefined) return '-';
  return Number(v).toFixed(2);
}

export default function RebillList() {
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const isFinanceOrAdmin = user.role === 'finance' || user.role === 'admin';
  const isCsOrAdmin = user.role === 'customer_service' || user.role === 'admin';

  const [keyword, setKeyword] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);

  const [detail, setDetail] = useState(null);
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [createForm] = Form.useForm();
  const [editForm] = Form.useForm();
  const [customers, setCustomers] = useState([]);
  const [csUsers, setCsUsers] = useState([]);

  const [toCsModal, setToCsModal] = useState(false);
  const [toCsForm] = Form.useForm();
  const [csReplyModal, setCsReplyModal] = useState(false);
  const [csReplyForm] = Form.useForm();
  const [closeModal, setCloseModal] = useState(false);
  const [closeForm] = Form.useForm();
  const [reopenModal, setReopenModal] = useState(false);
  const [reopenForm] = Form.useForm();
  const [current, setCurrent] = useState(null);

  // 手动关联/取消关联申诉
  const [linkModalOpen, setLinkModalOpen] = useState(false);
  const [linkSearch, setLinkSearch] = useState('');
  const [linkCandidates, setLinkCandidates] = useState([]);

  // 高亮详情：从 URL query 解析 highlight=rebill_id，打开对应详情抽屉
  const location = typeof window !== 'undefined' ? new URLSearchParams(window.location.search) : null;
  const highlightId = location ? parseInt(location.get('highlight'), 10) : NaN;

  const openDetailById = async (id) => {
    try {
      const full = await rebillApi.get(id);
      setDetail(full);
    } catch (e) { console.warn(e); }
  };

  useEffect(() => { loadList(); loadOptions(); }, [page, pageSize, statusFilter, keyword]);
  // highlight 查询
  useEffect(() => {
    if (highlightId > 0) openDetailById(highlightId);
  }, [highlightId]);

  const loadOptions = async () => {
    try {
      const [cust, cs] = await Promise.all([
        customerApi.listNames(),
        authApi.csUsers(),
      ]);
      // /api/customers/names 返回 [{id, name}]
      const arr = Array.isArray(cust) ? cust : [];
      // 兼容旧前端：把对象形式的 {id,name} 转成下拉需要的 {name}
      setCustomers(arr.map((c) => (typeof c === 'string' ? c : ({ ...c, name: c.name || c.label }))));
      setCsUsers(Array.isArray(cs) ? cs : []);
    } catch (e) { console.warn(e); }
  };

  const loadList = async () => {
    setLoading(true);
    try {
      const params = { skip: (page - 1) * pageSize, limit: pageSize };
      if (keyword) params.keyword = keyword;
      if (statusFilter) params.status = statusFilter;
      const res = await rebillApi.list(params);
      setItems(res.items || []);
      setTotal(res.total || 0);
    } catch (e) {
      console.warn(e);
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async () => {
    try {
      const values = await createForm.validateFields();
      await rebillApi.create(values);
      message.success('返账单已登记');
      createForm.resetFields();
      setCreateModalOpen(false);
      loadList();
    } catch (e) { console.warn(e); }
  };

  const handleUpdate = async () => {
    try {
      const values = await editForm.validateFields();
      await rebillApi.update(current.id, values);
      message.success('已更新');
      setEditModalOpen(false);
      loadList();
      setDetail({ ...current, ...values });
    } catch (e) { console.warn(e); }
  };

  const openDetail = async (row) => {
    try {
      const full = await rebillApi.get(row.id);
      setDetail(full);
    } catch (e) { console.warn(e); }
  };

  const openEdit = async (row) => {
    setCurrent(row);
    editForm.setFieldsValue({
      bol_no_raw: row.bol_no_raw, pro_no_raw: row.pro_no_raw,
      query_no_raw: row.query_no_raw, platform_no_raw: row.platform_no_raw,
      customer_name: row.customer_name, supplier: row.supplier,
      amount: row.amount, my_bear_amount: row.my_bear_amount,
      customer_bear_amount: row.customer_bear_amount, bearing_type: row.bearing_type,
      remark: row.remark, cs_owner_id: row.cs_owner_id,
    });
    setEditModalOpen(true);
  };

  const actionToCs = async () => {
    const v = await toCsForm.validateFields();
    await rebillApi.toCs(current.id, v);
    message.success('已交给客服');
    setToCsModal(false); toCsForm.resetFields();
    openDetail({ ...current }); loadList();
  };

  const actionCsReply = async () => {
    const v = await csReplyForm.validateFields();
    await rebillApi.csReply(current.id, v);
    message.success('客服已回复');
    setCsReplyModal(false); csReplyForm.resetFields();
    openDetail({ ...current }); loadList();
  };

  const actionClose = async () => {
    const v = await closeForm.validateFields();
    await rebillApi.close(current.id, v);
    message.success('已结案');
    setCloseModal(false); closeForm.resetFields();
    openDetail({ ...current }); loadList();
  };

  const actionReopen = async () => {
    const v = await reopenForm.validateFields();
    await rebillApi.reopen(current.id, v);
    message.success('已重新打开');
    setReopenModal(false); reopenForm.resetFields();
    openDetail({ ...current }); loadList();
  };

  const handleExport = async () => {
    try {
      const res = await rebillApi.exportExcel({
        status: statusFilter || undefined, keyword: keyword || undefined,
      });
      const blob = new Blob([res.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = `rebills_${dayjs().format('YYYYMMDD')}.xlsx`;
      a.click(); URL.revokeObjectURL(url);
    } catch (e) { console.warn(e); }
  };

  const columns = [
    { title: '返账单号', dataIndex: 'rebill_no', width: 160, fixed: 'left' },
    { title: '状态', dataIndex: 'status', width: 120,
      render: (s, r) => (
        <Space>
          <Tag color={STATUS_COLOR[s] || 'default'}>{STATUS_TEXT[s] || s}</Tag>
          {r.overdue && <Tag color="red">已超期</Tag>}
        </Space>
      ) },
    { title: '客户', dataIndex: 'customer_name', width: 140 },
    { title: 'BOL号', dataIndex: 'bol_no', width: 140 },
    { title: 'PRO号', dataIndex: 'pro_no', width: 140 },
    { title: '平台单号', dataIndex: 'platform_no', width: 140 },
    { title: '金额', dataIndex: 'amount', width: 100, render: v => amountFmt(v) },
    { title: '承担方式', dataIndex: 'bearing_type', width: 140, render: v => BEARING_TEXT[v] || v },
    { title: '轮次', dataIndex: 'round_no', width: 70 },
    { title: '截止时间', dataIndex: 'cs_due_at', width: 180,
      render: v => v ? dayjs(v).format('YYYY-MM-DD HH:mm') : '-' },
    { title: '创建时间', dataIndex: 'created_at', width: 180,
      render: v => v ? dayjs(v).format('YYYY-MM-DD HH:mm') : '-' },
    { title: '操作', key: 'op', width: 220, fixed: 'right',
      render: (_, row) => (
        <Space size={4}>
          <Button size="small" icon={<EyeOutlined />} onClick={() => openDetail(row)}>查看</Button>
          {isFinanceOrAdmin && row.status !== 'closed' && (
            <Button size="small" icon={<EditOutlined />} onClick={() => openEdit(row)}>编辑</Button>
          )}
          {isFinanceOrAdmin && row.status !== 'closed' && row.status !== 'awaiting_cs' && (
            <Button size="small" type="primary"
              onClick={() => { setCurrent(row); toCsForm.resetFields(); setToCsModal(true); }}>交客服</Button>
          )}
          {isCsOrAdmin && row.status === 'awaiting_cs' && (
            <Button size="small" type="primary"
              onClick={() => { setCurrent(row); csReplyForm.resetFields(); setCsReplyModal(true); }}>客服回复</Button>
          )}
          {isFinanceOrAdmin && row.status === 'cs_replied' && (
            <Button size="small" type="primary"
              onClick={() => { setCurrent(row); closeForm.resetFields(); setCloseModal(true); }}>结案</Button>
          )}
          {isFinanceOrAdmin && row.status === 'closed' && (
            <Button size="small"
              onClick={() => { setCurrent(row); reopenForm.resetFields(); setReopenModal(true); }}>重开</Button>
          )}
        </Space>
      ) },
  ];

  return (
    <div style={{ padding: 16 }}>
      <Card title="返账单登记" extra={
        <Space>
          {isFinanceOrAdmin && (
            <Button type="primary" icon={<PlusOutlined />}
              onClick={() => { createForm.resetFields(); setCreateModalOpen(true); }}>新建返账单</Button>
          )}
          {isFinanceOrAdmin && (
            <Button icon={<DownloadOutlined />} onClick={handleExport}>Excel 导出</Button>
          )}
        </Space>
      }>
        <Space style={{ marginBottom: 16 }} wrap>
          <Input.Search placeholder="搜索返账单号/BOL/PRO/客户" allowClear
            value={keyword} onChange={e => setKeyword(e.target.value)}
            onSearch={() => { setPage(1); loadList(); }} style={{ width: 280 }} />
          <Select placeholder="状态" allowClear style={{ width: 160 }}
            value={statusFilter} onChange={v => { setStatusFilter(v || ''); setPage(1); }}>
            {STATUS_OPTIONS.map(o => <Select.Option key={o.value} value={o.value}>{o.label}</Select.Option>)}
          </Select>
        </Space>
        <Spin spinning={loading}>
          <Table rowKey="id" size="small" scroll={{ x: 1600 }}
            columns={columns} dataSource={items}
            pagination={{ current: page, pageSize, total, showSizeChanger: true,
              onChange: (p, s) => { setPage(p); setPageSize(s); } }} />
        </Spin>
      </Card>

      {/* 详情 */}
      <Drawer title={`返账单详情 · ${detail?.rebill_no || ''}`} width={720}
        open={!!detail} onClose={() => setDetail(null)}>
        {detail && (
          <Space direction="vertical" style={{ width: '100%' }} size="large">
            <Descriptions column={2} bordered size="small">
              <Descriptions.Item label="状态">
                <Tag color={STATUS_COLOR[detail.status]}>{STATUS_TEXT[detail.status]}</Tag>
                {detail.overdue && <Tag color="red">已超期</Tag>}
              </Descriptions.Item>
              <Descriptions.Item label="轮次">{detail.round_no}</Descriptions.Item>
              <Descriptions.Item label="客户">{detail.customer_name}</Descriptions.Item>
              <Descriptions.Item label="供应商">{detail.supplier || '-'}</Descriptions.Item>
              <Descriptions.Item label="BOL号">{detail.bol_no_raw} → {detail.bol_no}</Descriptions.Item>
              <Descriptions.Item label="PRO号">{detail.pro_no_raw} → {detail.pro_no}</Descriptions.Item>
              <Descriptions.Item label="查询号">{detail.query_no_raw} → {detail.query_no}</Descriptions.Item>
              <Descriptions.Item label="平台单号">{detail.platform_no_raw} → {detail.platform_no}</Descriptions.Item>
              <Descriptions.Item label="总金额">{amountFmt(detail.amount)}</Descriptions.Item>
              <Descriptions.Item label="承担方式">{BEARING_TEXT[detail.bearing_type] || detail.bearing_type}</Descriptions.Item>
              <Descriptions.Item label="我方承担">{amountFmt(detail.my_bear_amount)}</Descriptions.Item>
              <Descriptions.Item label="客户承担">{amountFmt(detail.customer_bear_amount)}</Descriptions.Item>
              <Descriptions.Item label="最终确认金额">{detail.final_amount ? amountFmt(detail.final_amount) : '-'}</Descriptions.Item>
              <Descriptions.Item label="财务负责人">{detail.finance_owner_name || '-'}</Descriptions.Item>
              <Descriptions.Item label="客服接单人">{detail.cs_owner_name || '-'}</Descriptions.Item>
              <Descriptions.Item label="交客服时间">{detail.cs_handed_at ? dayjs(detail.cs_handed_at).format('YYYY-MM-DD HH:mm') : '-'}</Descriptions.Item>
              <Descriptions.Item label="截止时间">{detail.cs_due_at ? dayjs(detail.cs_due_at).format('YYYY-MM-DD HH:mm') : '-'}</Descriptions.Item>
              <Descriptions.Item label="客服回复" span={2}>{detail.cs_reply || '-'}</Descriptions.Item>
              <Descriptions.Item label="申诉结论" span={2}>
                {detail.appeal_conclusion ? <Paragraph style={{ whiteSpace: 'pre-wrap', margin: 0 }}>{detail.appeal_conclusion}</Paragraph> : '-'}
              </Descriptions.Item>
              <Descriptions.Item label="备注" span={2}>{detail.remark || '-'}</Descriptions.Item>
            </Descriptions>

            {/* 关联申诉区域：零关联也显示"手动搜索关联"入口，避免无法首次关联 */}
            {isFinanceOrAdmin && (
              <Card title="关联申诉" size="small" type="inner"
                extra={<Button size="small" icon={<LinkOutlined />}
                  onClick={() => { setCurrent(detail); setLinkCandidates([]); setLinkSearch(''); setLinkModalOpen(true); }}>
                    {detail.appeal_links?.length > 0 ? '手动搜索关联' : '+ 手动关联申诉'}
                  </Button>}
                >
                {detail.appeal_links?.length > 0 ? detail.appeal_links.map(l => (
                  <div key={l.id} style={{ marginBottom: 4, display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span>
                      <Tag color="blue">{l.appeal_no}</Tag> {l.match_source === 'auto' ? '自动' : '手动'}
                      <Tag color={l.appeal_status === 'completed' ? 'green' : 'gold'}>{STATUS_TEXT[l.appeal_status] || l.appeal_status}</Tag>
                    </span>
                    <Popconfirm title="确认取消此关联？" onConfirm={async () => {
                      try {
                        await rebillApi.unlinkAppeal(detail.id, l.appeal_id);
                        message.success('已取消关联');
                        openDetailById(detail.id);
                        loadList();
                      } catch (e) { message.error(e.message || '操作失败'); }
                    }}>
                      <Button size="small" type="link" danger icon={<DisconnectOutlined />}>取消关联</Button>
                    </Popconfirm>
                  </div>
                )) : (
                  <div style={{ color: '#8c8c8c', fontSize: 13 }}>
                    暂无关联申诉；点击右上角"+ 手动关联申诉"选择目标。
                  </div>
                )}
              </Card>
            )}

            {detail.amount_audits?.length > 0 && (
              <Card title="金额审计" size="small" type="inner">
                <Table size="small" pagination={false} rowKey="id" scroll={{ x: 600 }}
                  columns={[
                    { title: '字段', dataIndex: 'field_name' },
                    { title: '旧值', dataIndex: 'old_value', render: v => v ?? '-' },
                    { title: '新值', dataIndex: 'new_value', render: v => v ?? '-' },
                    { title: '操作人', dataIndex: 'operator_name' },
                    { title: '时间', dataIndex: 'created_at', render: v => v ? dayjs(v).format('YYYY-MM-DD HH:mm') : '-' },
                  ]} dataSource={detail.amount_audits} />
              </Card>
            )}

            {detail.logs?.length > 0 && (
              <Card title="操作日志" size="small" type="inner">
                <Table size="small" pagination={false} rowKey="id" scroll={{ x: 800 }}
                  columns={[
                    { title: '时间', dataIndex: 'created_at', width: 160,
                      render: v => v ? dayjs(v).format('YYYY-MM-DD HH:mm:ss') : '-' },
                    { title: '动作', dataIndex: 'action', width: 110 },
                    { title: '操作人', dataIndex: 'operator_name', width: 120 },
                    { title: '详情', dataIndex: 'content',
                      render: (v, r) => (
                        <div>
                          {r.field_changed && <Text type="secondary">{r.field_changed}: {r.old_value || '-'} → {r.new_value || '-'}</Text>}
                          {r.content && <div>{r.content}</div>}
                        </div>
                      ) },
                  ]} dataSource={[...detail.logs].reverse()} />
              </Card>
            )}
          </Space>
        )}
      </Drawer>

      {/* 新建 */}
      <Modal open={createModalOpen} title="新建返账单登记" width={640}
        onCancel={() => setCreateModalOpen(false)} onOk={handleCreate}
        destroyOnClose>
        <Form form={createForm} layout="vertical">
          <Form.Item name="customer_name" label="客户" rules={[{ required: true, message: '客户必填' }]}>
            <Select showSearch placeholder="搜索客户" filterOption={(k, o) => o.children?.toLowerCase().includes(k.toLowerCase())}>
              {customers.map(c => <Select.Option key={c.name} value={c.name}>{c.name}</Select.Option>)}
            </Select>
          </Form.Item>
          <Form.Item name="supplier" label="供应商">
            <Input placeholder="供应商/渠道" />
          </Form.Item>
          <Form.Item name="bol_no_raw" label="BOL号">
            <Input placeholder="原始值，去空格+大写规范化" />
          </Form.Item>
          <Form.Item name="pro_no_raw" label="PRO号">
            <Input />
          </Form.Item>
          <Form.Item name="query_no_raw" label="查询号">
            <Input />
          </Form.Item>
          <Form.Item name="platform_no_raw" label="平台单号">
            <Input placeholder="字符串形态保留，不转数字" />
          </Form.Item>
          <Form.Item name="amount" label="返账单总金额" rules={[{ required: true, message: '必填' }]}>
            <InputNumber min={-10000000} step={0.01} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="bearing_type" label="承担方式">
            <Select>
              {BEARING_OPTIONS.map(o => <Select.Option key={o.value} value={o.value}>{o.label}</Select.Option>)}
            </Select>
          </Form.Item>
          <Form.Item name="my_bear_amount" label="我方承担金额"><InputNumber min={0} step={0.01} style={{ width: '100%' }} /></Form.Item>
          <Form.Item name="customer_bear_amount" label="客户承担金额"><InputNumber min={0} step={0.01} style={{ width: '100%' }} /></Form.Item>
          <Form.Item name="remark" label="备注"><Input.TextArea rows={3} /></Form.Item>
        </Form>
      </Modal>

      {/* 编辑（简化：基础字段 + 承担方式 + 金额分摊 + 最终金额 + 备注）*/}
      <Modal open={editModalOpen} title="编辑返账单" width={640}
        onCancel={() => setEditModalOpen(false)} onOk={handleUpdate} destroyOnClose>
        <Form form={editForm} layout="vertical">
          <Form.Item name="customer_name" label="客户">
            <Select showSearch>{customers.map(c => <Select.Option key={c.name} value={c.name}>{c.name}</Select.Option>)}</Select>
          </Form.Item>
          <Form.Item name="supplier" label="供应商"><Input /></Form.Item>
          <Form.Item name="bol_no_raw" label="BOL号原始值"><Input /></Form.Item>
          <Form.Item name="pro_no_raw" label="PRO号原始值"><Input /></Form.Item>
          <Form.Item name="platform_no_raw" label="平台单号原始值"><Input /></Form.Item>
          <Form.Item name="amount" label="总金额"><InputNumber min={-10000000} step={0.01} style={{ width: '100%' }} /></Form.Item>
          <Form.Item name="bearing_type" label="承担方式">
            <Select>{BEARING_OPTIONS.map(o => <Select.Option key={o.value} value={o.value}>{o.label}</Select.Option>)}</Select>
          </Form.Item>
          <Form.Item name="my_bear_amount" label="我方承担"><InputNumber min={0} step={0.01} style={{ width: '100%' }} /></Form.Item>
          <Form.Item name="customer_bear_amount" label="客户承担"><InputNumber min={0} step={0.01} style={{ width: '100%' }} /></Form.Item>
          <Form.Item name="final_amount" label="最终确认金额"><InputNumber step={0.01} style={{ width: '100%' }} /></Form.Item>
          <Form.Item name="remark" label="备注"><Input.TextArea rows={3} /></Form.Item>
        </Form>
      </Modal>

      {/* 交客服 */}
      <Modal open={toCsModal} title="财务交客服" onCancel={() => setToCsModal(false)}
        onOk={actionToCs}>
        <Form form={toCsForm} layout="vertical">
          <Form.Item name="cs_owner_id" label="指派客服接单人">
            <Select placeholder="可选，若不选则任意客服可回复" allowClear>
              {csUsers.map(u => <Select.Option key={u.id} value={u.id}>{u.full_name || u.username}</Select.Option>)}
            </Select>
          </Form.Item>
          <Form.Item name="note" label="交客服说明"><Input.TextArea rows={3} /></Form.Item>
          <Paragraph type="secondary">截止时间 = 交客服时间 + 72 小时（自然日）</Paragraph>
        </Form>
      </Modal>

      {/* 客服回复 */}
      <Modal open={csReplyModal} title="客服回复" onCancel={() => setCsReplyModal(false)}
        onOk={actionCsReply}>
        <Form form={csReplyForm} layout="vertical">
          <Form.Item name="cs_reply" label="处理意见" rules={[{ required: true, message: '请填写回复内容' }]}>
            <Input.TextArea rows={4} />
          </Form.Item>
          <Form.Item name="bearing_type" label="承担方式（可选）">
            <Select allowClear>{BEARING_OPTIONS.map(o => <Select.Option key={o.value} value={o.value}>{o.label}</Select.Option>)}</Select>
          </Form.Item>
          <Form.Item name="my_bear_amount" label="我方承担金额（可选）"><InputNumber min={0} step={0.01} style={{ width: '100%' }} /></Form.Item>
          <Form.Item name="customer_bear_amount" label="客户承担金额（可选）"><InputNumber min={0} step={0.01} style={{ width: '100%' }} /></Form.Item>
        </Form>
      </Modal>

      {/* 结案 */}
      <Modal open={closeModal} title="财务结案" onCancel={() => setCloseModal(false)}
        onOk={actionClose}>
        <Form form={closeForm} layout="vertical">
          <Form.Item name="final_amount" label="最终确认金额"><InputNumber step={0.01} style={{ width: '100%' }} /></Form.Item>
          <Form.Item name="remark" label="结案备注"><Input.TextArea rows={3} /></Form.Item>
        </Form>
      </Modal>

      {/* 重开 */}
      <Modal open={reopenModal} title="财务重新打开" onCancel={() => setReopenModal(false)}
        onOk={actionReopen}>
        <Form form={reopenForm} layout="vertical">
          <Form.Item name="reason" label="重开原因" rules={[{ required: true, message: '必填' }]}>
            <Input.TextArea rows={3} />
          </Form.Item>
        </Form>
      </Modal>

      {/* 手动搜索关联申诉 */}
      <Modal
        open={linkModalOpen}
        title={`手动搜索关联申诉 —— 返账单 ${current?.rebill_no || ''}`}
        width={640}
        onCancel={() => setLinkModalOpen(false)}
        footer={null}
        destroyOnClose
      >
        <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
          <Input.Search
            placeholder="搜索申诉单号 / 客户 / 查询号 / BOL"
            allowClear
            style={{ flex: 1 }}
            value={linkSearch}
            onChange={(e) => setLinkSearch(e.target.value)}
            onSearch={async (kw) => {
              if (!kw) { setLinkCandidates([]); return; }
              try {
                const res = await appealApi.list({ keyword: kw, limit: 20 });
                setLinkCandidates(res.items || []);
              } catch (e) { message.error(e.message || '搜索失败'); }
            }}
          />
        </div>
        <Spin spinning={linkCandidates.length === 0 && linkSearch}>
          <div style={{ maxHeight: 360, overflow: 'auto' }}>
            {linkCandidates.length === 0 ? (
              <div style={{ color: '#999', textAlign: 'center', padding: 24 }}>{linkSearch ? '无匹配申诉' : '输入关键词搜索'}</div>
            ) : linkCandidates.map((a) => (
              <div key={a.id} style={{ padding: '8px 12px', borderBottom: '1px solid #f5f5f5', display: 'flex', alignItems: 'center', gap: 8 }}>
                <div style={{ flex: 1 }}>
                  <div><Tag color="blue">{a.appeal_no}</Tag> {a.kewell_track || ''}</div>
                  <div style={{ fontSize: 12, color: '#888' }}>客户 {a.customer_name} · 状态 {a.status}</div>
                </div>
                <Button
                  type="primary"
                  size="small"
                  disabled={(detail?.appeal_links || []).some((l) => l.appeal_id === a.id)}
                  onClick={async () => {
                    try {
                      await rebillApi.linkAppeal(current.id, a.id);
                      message.success(`已关联 ${a.appeal_no}`);
                      setLinkModalOpen(false);
                      openDetailById(current.id);
                      loadList();
                    } catch (e) { message.error(e.message || '关联失败'); }
                  }}
                >关联</Button>
              </div>
            ))}
          </div>
        </Spin>
      </Modal>
    </div>
  );
}
